import { useMemo, useState } from "react";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { ModalTypes } from "@enums/modal-types";
import { sendVerificationMessageRequest } from "@requests/service-configuration";
import { CopyToClipboardWrapper } from "../../common/copy-to-clipboard-wrapper/copy-to-clipboard-wrapper";
import { DefaultModal } from "../../common/modals/default-modal/default-modal";
import { ButtonWithSpinner } from "../../common/button-with-spinner/button-with-spinner";
import styles from "./copy-link-result-modal.less";
import defaultStyles from "../../common/modals/default-modal/default-modal.less";
import { resources } from "@constants/hardcoded-strings";
import { ModalSizeTypes } from "@enums/modal-size-types";
import { ButtonTypes } from "@enums/button-types";

type VerificationLinkProviderResultModalProps = {
    onModalClose: () => void;
    onClickResetPII: (onModalClose: () => void) => void;
    link: string;
    recipient?: string;
    selectedLanguage?: string;
    messageTemplateId?: string;
    modalType: ModalTypes;
    rpServiceId: string;
    idp?: string;
    country?: string;
    redirectTo?: string;
    credential?: string;
    asi?: string;
    appId?: string;
    attributeTypeToFieldValueMap: { [id: string]: { [id: string]: string } };
    canModifySelfServiceVerificationExpiration: boolean;
    loginHintExpirationType: string;
    loginHintExpirationValue: string;
};

export const VerificationLinkProviderResultModal = (props: VerificationLinkProviderResultModalProps) => {
    const {
        onModalClose,
        onClickResetPII,
        link,
        recipient,
        selectedLanguage,
        messageTemplateId,
        modalType,
        rpServiceId,
        idp,
        country,
        redirectTo,
        credential,
        asi,
        appId,
        attributeTypeToFieldValueMap,
        canModifySelfServiceVerificationExpiration,
        loginHintExpirationType,
        loginHintExpirationValue
    } = props;

    const [requestInProgress, setRequestInProgress] = useState(false);

    const handleOnClickResetPII = () => {
        onClickResetPII(onModalClose);
    };

    const handleOnClickOpenInNewWindow = () => {
        window.open(link, "_blank");
    };

    const handleOnClickResendEmail = async () => {
        if (recipient && messageTemplateId && selectedLanguage) {
            setRequestInProgress(true);
            await sendVerificationMessageRequest(
                messageTemplateId,
                selectedLanguage,
                recipient,
                rpServiceId,
                idp,
                country,
                redirectTo,
                credential,
                asi,
                appId,
                attributeTypeToFieldValueMap,
                canModifySelfServiceVerificationExpiration,
                loginHintExpirationType,
                loginHintExpirationValue
            );
            setRequestInProgress(false);
        }
    };

    const description = useMemo(() => {
        switch (modalType) {
            case ModalTypes.TryNowResult:
                return resources.modals.verificationLinkProviderResult.descriptions.tryNow;
            case ModalTypes.CopyLinkResult:
                return resources.modals.verificationLinkProviderResult.descriptions.copy;
            case ModalTypes.SendMessageWithLinkResult:
                return resources.modals.verificationLinkProviderResult.descriptions.sendMessage;
            default:
                return "";
        }
    }, [modalType]);

    return (
        <DefaultModal
            automationId="verification-link-provider-result-modal"
            ariaLabelledBy="verification-link-provider-link-result-title"
            onHide={onModalClose}
            backdrop={"static"}
            keyboard={false}
            isMaxWidth={false}
            // @ts-ignore
            size={ModalSizeTypes.MEDIUM}
        >
            <Modal.Header id={"verification-link-provider-result-title"} closeButton>
                <Modal.Title>{resources.modals.verificationLinkProviderResult.title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className={defaultStyles.description}>{description}</div>
                <div className={styles.copyDiv}>
                    <Form.Control defaultValue={link} disabled={true} />
                    <CopyToClipboardWrapper
                        title={resources.common.buttons.copy.label}
                        label={resources.common.buttons.copy.label}
                        text={link}
                        placement={"top"}
                    />
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onModalClose}>{resources.common.buttons.close.label}</Button>
                <Button variant="secondary" onClick={handleOnClickResetPII}>
                    {resources.modals.verificationLinkProviderResult.buttons.resetPii.label}
                </Button>
                {modalType === ModalTypes.TryNowResult && (
                    <Button variant="primary" onClick={handleOnClickOpenInNewWindow}>
                        {resources.modals.verificationLinkProviderResult.buttons.openInNewWindow.label}
                    </Button>
                )}
                {modalType === ModalTypes.SendMessageWithLinkResult && (
                    <ButtonWithSpinner
                        disabled={requestInProgress}
                        label={resources.modals.verificationLinkProviderResult.buttons.resendMessage.label}
                        type={ButtonTypes.BUTTON}
                        variant="primary"
                        onClick={handleOnClickResendEmail}
                    />
                )}
            </Modal.Footer>
        </DefaultModal>
    );
};